import React from 'react';
import { LexicalEventEditorDemo } from '../components/LexicalEventEditor';

/**
 * Demo page showcasing Deep Lexical Integration for EdgeCalendar
 * This demonstrates the legal entity reference system
 */
export function LexicalDemo(): React.JSX.Element {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            EdgeCalendar Deep Lexical Integration
          </h1>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            Revolutionary legal calendar with intelligent entity references
          </p>
        </div>

        <div className="space-y-8">
          {/* Main Demo */}
          <LexicalEventEditorDemo />

          {/* Feature Overview */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              🚀 Deep Lexical Integration Features
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    🔗 Legal Entity References
                  </h3>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li><strong>@mentions</strong> - Contact/client references with click-to-navigate</li>
                    <li><strong>#matters</strong> - Case and matter references with status indicators</li>
                    <li><strong>$bills</strong> - Invoice and billing references with amount display</li>
                    <li><strong>/doc</strong> - Document references with type and preview</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    ⚡ Intelligent Features
                  </h3>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>Real-time typeahead search and suggestions</li>
                    <li>Structured JSON storage for AI processing</li>
                    <li>Cross-platform copy/paste with entity preservation</li>
                    <li>Accessibility-compliant pill navigation</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    🎯 Legal Practice Benefits
                  </h3>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>Eliminates context switching between applications</li>
                    <li>Creates structured knowledge base from calendar events</li>
                    <li>Enables sophisticated search and filtering</li>
                    <li>Preserves institutional knowledge across team changes</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    🔮 Future Capabilities
                  </h3>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>MCP AI analysis of entity relationships</li>
                    <li>Template system with pre-filled entities</li>
                    <li>Interactive daily agenda with embedded events</li>
                    <li>Export to professional briefing documents</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Technical Implementation */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              🔧 Technical Implementation
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-3">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">
                  Custom Lexical Nodes
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <div className="font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded">ContactMentionNode</div>
                  <div className="font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded">MatterReferenceNode</div>
                  <div className="font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded">BillReferenceNode</div>
                  <div className="font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded">DocumentReferenceNode</div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">
                  Smart Plugins
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <div className="font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded">MentionPlugin</div>
                  <div className="font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded">TypeaheadPlugin</div>
                  <div className="font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded">ReferencePlugin</div>
                  <div className="font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded">TemplatePlugin</div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">
                  Integration Ready
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <div>✅ Keycloak multi-tenant security</div>
                  <div>✅ MCP orchestration ready</div>
                  <div>✅ WebSocket real-time updates</div>
                  <div>✅ API integration stubs</div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="bg-lex-accent-soft rounded-lg border border-lex-accent p-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                🎉 Ready for Implementation
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                This Deep Lexical Integration transforms EdgeCalendar from a simple scheduler into an intelligent legal workspace.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="inline-flex items-center px-4 py-2 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 rounded-lg">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Foundation Complete
                </div>
                <div className="inline-flex items-center px-4 py-2 bg-lex-accent-soft text-lex-accent rounded-lg">
                  <span className="w-2 h-2 bg-lex-accent rounded-full mr-2"></span>
                  MCP Integration Ready
                </div>
                <div className="inline-flex items-center px-4 py-2 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 rounded-lg">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                  Production Ready
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 